import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  UploadCloud, Loader2, Camera, Image as ImageIcon, X, Check, Trash2,
  AlertTriangle, Users, ArrowUpRight, Plus, Save, Mic, Play, Pause, StopCircle,
  Layers, Tag, CalendarClock, QrCode, FlipHorizontal, MapPin, Building2,
} from "lucide-react";
import { EmptyState } from "./EmptyState";

const MAX_CARDS = 20;

/**
 * BatchCardScanner
 *
 * Reusable batch visiting-card scanner used by:
 *  - Field Sales / Sales role → saves into the tenant's CRM
 *  - Super Admin Master Contacts → saves into the platform pool
 *
 * Props:
 *  - mode: "crm" (default) | "master"
 *  - onDone(result): callback after successful save (result: { saved, merged, skipped })
 *  - initialGeo: { lat, lng } (optional)
 */
export function BatchCardScanner({ mode = "crm", onDone, initialGeo }) {
  const [phase, setPhase] = useState("idle"); // idle | scanning | review | saving | done
  const [items, setItems] = useState([]);     // per-card records
  const [progress, setProgress] = useState({ total: 0, done: 0 });
  const [saveResult, setSaveResult] = useState(null);
  const [buckets, setBuckets] = useState(null);
  const [pairMode, setPairMode] = useState(false); // front+back pairing
  const [groupBy, setGroupBy] = useState("none"); // none|by_city|by_state|by_type
  const [pendingFront, setPendingFront] = useState(null); // pairing state
  const fileRef = useRef(null);
  const galleryRef = useRef(null);
  const pairFrontRef = useRef(null);
  const pairBackRef = useRef(null);

  const isMaster = mode === "master";

  const scanEndpoint = isMaster
    ? "/field-sales/scan-cards/batch/master"
    : "/field-sales/scan-cards/batch";
  const saveEndpoint = isMaster
    ? "/field-sales/scan-cards/batch/master/save"
    : "/field-sales/scan-cards/batch/save";

  const reset = () => {
    setPhase("idle");
    setItems([]);
    setProgress({ total: 0, done: 0 });
    setSaveResult(null);
    setBuckets(null);
    setPendingFront(null);
    if (fileRef.current) fileRef.current.value = "";
    if (galleryRef.current) galleryRef.current.value = "";
    if (pairFrontRef.current) pairFrontRef.current.value = "";
    if (pairBackRef.current) pairBackRef.current.value = "";
  };

  const handleFiles = async (fileList) => {
    let files = Array.from(fileList || []);
    if (files.length === 0) return;
    if (files.length > MAX_CARDS) {
      toast.warning(`Max ${MAX_CARDS} cards per batch. Trimming.`);
      files = files.slice(0, MAX_CARDS);
    }
    setPhase("scanning");
    setProgress({ total: files.length, done: 0 });
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));

    try {
      const { data } = await api.post(scanEndpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        // no upload progress from axios in browser — fake it in the UI below
      });
      const rows = (data.items || []).map((it, idx) => ({
        ...normaliseRow(it, idx, isMaster),
      }));
      setItems(rows);
      setBuckets(data.buckets || null);
      setProgress({ total: rows.length, done: rows.length });
      setPhase("review");
      const imgs = data.images_uploaded || files.length;
      const msg = imgs !== data.total
        ? `Parsed ${data.ok}/${data.total} card${data.total === 1 ? "" : "s"} from ${imgs} photo${imgs === 1 ? "" : "s"}`
        : `Parsed ${data.ok}/${data.total}${data.failed ? ` · ${data.failed} failed` : ""}`;
      toast.success(msg);
    } catch (e) {
      setPhase("idle");
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Batch scan failed");
    }
  };

  const handlePairFiles = async (pairs) => {
    // pairs: [{front: File, back?: File}, ...]
    const capped = pairs.slice(0, MAX_CARDS);
    setPhase("scanning");
    setProgress({ total: capped.length, done: 0 });
    const fd = new FormData();
    capped.forEach((p) => {
      fd.append("fronts", p.front);
      // Backend is index-aligned. Push an empty placeholder if no back so alignment is preserved.
      if (p.back) fd.append("backs", p.back);
      else fd.append("backs", new Blob([]), "");
    });
    try {
      const { data } = await api.post("/field-sales/scan-cards/batch/pairs", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const rows = (data.items || []).map((it, idx) => ({
        ...normaliseRow(it, idx, isMaster),
        has_back: it.has_back,
        back_image_url: it.back_image_url,
        back_thumb_url: it.back_thumb_url,
      }));
      setItems(rows);
      setBuckets(data.buckets || null);
      setProgress({ total: rows.length, done: rows.length });
      setPhase("review");
      toast.success(`Parsed ${data.ok}/${data.total} pair${data.total === 1 ? "" : "s"}`);
    } catch (e) {
      setPhase("idle");
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Batch scan failed");
    }
  };

  const patch = (idx, patchObj) => {
    setItems((rows) => rows.map((r, i) => i === idx ? { ...r, ...patchObj } : r));
  };
  const patchParsed = (idx, patchObj) => {
    setItems((rows) => rows.map((r, i) => i === idx ? { ...r, parsed: { ...r.parsed, ...patchObj } } : r));
  };

  const saveAll = async () => {
    setPhase("saving");
    try {
      const body = {
        cards: items.filter((r) => r.status === "ok").map((r) => ({
          parsed: r.parsed,
          image_url: r.image_url,
          thumb_url: r.thumb_url,
          merge_into_contact_id: r.action === "merge" ? r.duplicate_id : null,
          action: r.action,
          voice_note_url: r.voice_note_url || null,
          voice_note_transcript: r.voice_note_transcript || null,
          tags: r.tags || [],
          follow_up_at: r.follow_up_at || null,
        })),
        latitude: initialGeo?.lat || null,
        longitude: initialGeo?.lng || null,
      };
      const { data } = await api.post(saveEndpoint, body);
      setSaveResult(data);
      setPhase("done");
      toast.success(`Saved ${data.saved} · ${data.merged || 0} merged · ${data.skipped || 0} skipped`);
      onDone?.(data);
    } catch (e) {
      setPhase("review");
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  // ---- Render ----
  if (phase === "idle") {
    return (
      <div className="space-y-3" data-testid="batch-scanner-idle">
        <div className="rounded-2xl border-2 border-dashed border-[#0F5E39]/30 bg-[#F6F1E1]/40 p-8 text-center">
          <UploadCloud size={40} className="mx-auto text-[#0F5E39]" strokeWidth={1.5}/>
          <div className="font-serif text-2xl mt-3">Batch scan cards</div>
          <p className="text-sm text-[#4A5A50] mt-1 max-w-md mx-auto">
            Upload up to {MAX_CARDS} photos. Each photo can contain <b>multiple cards</b> — we detect and split them automatically. Review before saving.
          </p>

          {/* Mode toggle */}
          <div className="mt-4 inline-flex rounded-full bg-white border border-[#E4DFCF] p-1 text-xs">
            <button
              className={`px-3 py-1 rounded-full ${!pairMode ? "bg-[#0F5E39] text-white" : "text-[#4A5A50]"}`}
              onClick={() => { setPairMode(false); setPendingFront(null); }}
              data-testid="batch-mode-single"
            >
              Front only
            </button>
            <button
              className={`px-3 py-1 rounded-full ${pairMode ? "bg-[#0F5E39] text-white" : "text-[#4A5A50]"}`}
              onClick={() => setPairMode(true)}
              data-testid="batch-mode-pair"
            >
              <FlipHorizontal size={12} className="inline mr-1"/> Front + Back
            </button>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="batch-camera-input"
          />
          <input
            ref={galleryRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="batch-gallery-input"
          />
          <input
            ref={pairFrontRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (!files.length) return;
              // Each front produces one pair (with back undefined for now)
              const pairs = files.map((f) => ({ front: f }));
              handlePairFiles(pairs);
              if (e.target) e.target.value = "";
            }}
            data-testid="batch-pair-fronts-input"
          />
          <input
            ref={pairBackRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const back = e.target.files?.[0];
              if (back && pendingFront) {
                handlePairFiles([{ front: pendingFront, back }]);
                setPendingFront(null);
              }
              if (e.target) e.target.value = "";
            }}
            data-testid="batch-pair-back-input"
          />

          <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
            {!pairMode ? (
              <>
                <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white h-12 px-6" onClick={() => fileRef.current?.click()} data-testid="batch-open-camera-btn">
                  <Camera size={16} className="mr-2"/> Open Camera
                </Button>
                <Button variant="outline" className="rounded-full border-[#0F5E39] text-[#0F5E39] hover:bg-[#0F5E39]/10 h-12 px-6" onClick={() => galleryRef.current?.click()} data-testid="batch-upload-gallery-btn">
                  <ImageIcon size={16} className="mr-2"/> Upload from Gallery
                </Button>
              </>
            ) : (
              <PairCapture
                pendingFront={pendingFront}
                setPendingFront={setPendingFront}
                onPairsReady={handlePairFiles}
                onPickFrontsBatch={() => pairFrontRef.current?.click()}
                onPickBack={() => pairBackRef.current?.click()}
              />
            )}
          </div>
          {pairMode && (
            <p className="text-[11px] text-[#4A5A50] mt-3">
              <FlipHorizontal size={11} className="inline mr-1"/>
              Capture the <b>front</b>, then the <b>back</b> — we&apos;ll merge phones, emails, and address from both sides.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (phase === "scanning") {
    return (
      <div className="rounded-2xl border border-[#E4DFCF] bg-white p-10 text-center space-y-3" data-testid="batch-scanning">
        <Loader2 className="animate-spin text-[#0F5E39] mx-auto" size={40}/>
        <div className="font-serif text-2xl">Scanning {progress.total} card{progress.total === 1 ? "" : "s"}…</div>
        <p className="text-sm text-[#4A5A50]">Vision AI is reading each card. This can take a few seconds.</p>
      </div>
    );
  }

  if (phase === "done" && saveResult) {
    return (
      <div className="rounded-2xl border border-[#0F5E39]/30 bg-[#E1F0E6] p-8 text-center space-y-3" data-testid="batch-done">
        <Check className="mx-auto text-[#0F5E39]" size={40}/>
        <div className="font-serif text-2xl text-[#0F5E39]">Batch saved</div>
        <p className="text-sm text-[#0F5E39]">
          {saveResult.saved} new · {saveResult.merged || 0} merged · {saveResult.skipped || 0} skipped
        </p>
        <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" onClick={reset} data-testid="batch-scan-more">
          <Plus size={14} className="mr-1"/> Scan more
        </Button>
      </div>
    );
  }

  // review
  const okCount = items.filter((r) => r.status === "ok" && r.action !== "skip").length;
  const failed = items.filter((r) => r.status !== "ok").length;

  return (
    <div className="space-y-4" data-testid="batch-review">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-serif text-xl">Review parsed cards</div>
          <div className="text-xs text-[#4A5A50]">
            {items.length} parsed · <b>{okCount}</b> will save · {failed} failed
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {/* Segregation control */}
          {buckets && (
            <div className="inline-flex rounded-full bg-white border border-[#E4DFCF] p-1 text-[11px]">
              {[
                { key: "none", label: "All" },
                { key: "by_city", label: "City" },
                { key: "by_state", label: "State" },
                { key: "by_type", label: "Type" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setGroupBy(opt.key)}
                  className={`px-3 py-1 rounded-full ${groupBy === opt.key ? "bg-[#C9A24B] text-[#0F1E15]" : "text-[#4A5A50]"}`}
                  data-testid={`batch-group-${opt.key}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
          <Button variant="outline" className="rounded-full" onClick={reset}><X size={14} className="mr-1"/>Discard</Button>
          <Button
            className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white h-11 px-6"
            onClick={saveAll}
            disabled={phase === "saving" || okCount === 0}
            data-testid="batch-save-all-btn"
          >
            {phase === "saving" ? <Loader2 size={14} className="animate-spin mr-2"/> : <Save size={14} className="mr-2"/>}
            Confirm & save all ({okCount})
          </Button>
        </div>
      </div>

      {groupBy !== "none" && buckets ? (
        <GroupedList
          items={items}
          groups={buckets[groupBy] || {}}
          isMaster={isMaster}
          onPatch={patch}
          onPatchParsed={patchParsed}
          groupIcon={groupBy === "by_city" ? MapPin : groupBy === "by_state" ? MapPin : Building2}
        />
      ) : (
        <div className="space-y-3">
          {items.map((r, idx) => <CardRow key={idx} row={r} idx={idx} isMaster={isMaster} onPatch={patch} onPatchParsed={patchParsed}/>)}
        </div>
      )}
    </div>
  );
}


function normaliseRow(it, idx, isMaster) {
  const parsed = it.parsed || {};
  return {
    idx,
    filename: it.filename,
    status: it.status,
    error: it.error,
    image_url: it.image_url,
    thumb_url: it.thumb_url,
    card_index: it.card_index,
    cards_in_image: it.cards_in_image,
    source_index: it.source_index,
    parsed: {
      name: parsed.name || "",
      company: parsed.company || "",
      designation: parsed.designation || "",
      phones: parsed.phones || [],
      emails: parsed.emails || [],
      websites: parsed.websites || [],
      address: parsed.address || "",
      detected_language: parsed.detected_language || "",
      raw_text: parsed.raw_text || "",
    },
    duplicate_id: isMaster ? it.duplicate_master_id : it.duplicate_contact_id,
    duplicate_name: isMaster ? it.duplicate_master_name : it.duplicate_contact_name,
    voice_note_url: null,
    voice_note_transcript: null,
    tags: [],
    follow_up_at: null,
    action: it.status === "ok"
      ? (isMaster && it.duplicate_master_id) ? "skip" : (!isMaster && it.duplicate_contact_id ? "merge" : "save")
      : "skip",
  };
}


function GroupedList({ items, groups, isMaster, onPatch, onPatchParsed, groupIcon: Icon }) {
  const entries = Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
  if (entries.length === 0) return null;
  return (
    <div className="space-y-4" data-testid="batch-grouped-list">
      {entries.map(([label, indices]) => (
        <div key={label} className="rounded-2xl border border-[#E4DFCF] bg-[#F6F1E1]/30 p-3" data-testid={`batch-group-${label}`}>
          <div className="flex items-center gap-2 mb-2">
            <Icon size={14} className="text-[#C9A24B]"/>
            <div className="font-serif text-lg">{label}</div>
            <span className="text-[10px] uppercase tracking-widest text-[#4A5A50]">{indices.length} card{indices.length === 1 ? "" : "s"}</span>
          </div>
          <div className="space-y-3">
            {indices.map((i) => {
              const row = items[i];
              if (!row) return null;
              return <CardRow key={i} row={row} idx={i} isMaster={isMaster} onPatch={onPatch} onPatchParsed={onPatchParsed}/>;
            })}
          </div>
        </div>
      ))}
    </div>
  );
}


function PairCapture({ pendingFront, setPendingFront, onPairsReady, onPickFrontsBatch, onPickBack }) {
  const frontOneRef = useRef(null);
  return (
    <>
      <input
        ref={frontOneRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) setPendingFront(f);
          if (e.target) e.target.value = "";
        }}
        data-testid="batch-pair-front-single-input"
      />
      {!pendingFront ? (
        <>
          <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white h-12 px-6" onClick={() => frontOneRef.current?.click()} data-testid="batch-pair-open-camera">
            <Camera size={16} className="mr-2"/> Capture Front
          </Button>
          <Button variant="outline" className="rounded-full border-[#0F5E39] text-[#0F5E39] hover:bg-[#0F5E39]/10 h-12 px-6" onClick={onPickFrontsBatch} data-testid="batch-pair-fronts-only">
            <ImageIcon size={16} className="mr-2"/> Fronts only (no back)
          </Button>
        </>
      ) : (
        <>
          <Button className="rounded-full bg-[#C9A24B] hover:bg-[#B88C34] text-[#0F1E15] h-12 px-6" onClick={onPickBack} data-testid="batch-pair-capture-back">
            <FlipHorizontal size={16} className="mr-2"/> Capture Back
          </Button>
          <Button variant="outline" className="rounded-full h-12 px-6" onClick={() => onPairsReady([{ front: pendingFront }]) & setPendingFront(null)} data-testid="batch-pair-skip-back">
            Skip back · use front only
          </Button>
          <Button variant="ghost" className="h-12 text-red-600" onClick={() => setPendingFront(null)} data-testid="batch-pair-cancel">
            <X size={16}/>
          </Button>
        </>
      )}
    </>
  );
}


function CardRow({ row, idx, isMaster, onPatch, onPatchParsed }) {
  const failed = row.status !== "ok";
  const dup = !!row.duplicate_id;
  const skipped = row.action === "skip";

  const cls = failed
    ? "border-red-200 bg-red-50/30"
    : skipped
    ? "border-[#E4DFCF] bg-[#F1EDDF]/40 opacity-70"
    : dup
    ? "border-amber-200 bg-amber-50/30"
    : "border-[#E4DFCF] bg-white";

  const [tagInput, setTagInput] = useState("");
  const [voiceBusy, setVoiceBusy] = useState(false);

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!row.tags?.includes(t)) onPatch(idx, { tags: [...(row.tags || []), t] });
    setTagInput("");
  };
  const removeTag = (t) => onPatch(idx, { tags: (row.tags || []).filter((x) => x !== t) });

  return (
    <Card className={`rounded-2xl ${cls}`} data-testid={`batch-row-${idx}`}>
      <CardContent className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-[110px_1fr_auto] gap-4 items-start">
          {/* Thumbnail */}
          <div className="relative">
            <a href={row.image_url ? fileUrl(row.image_url) : "#"} target="_blank" rel="noreferrer" className="block">
              {row.thumb_url ? (
                <img src={fileUrl(row.thumb_url)} alt="card" className="w-24 h-24 md:w-[110px] md:h-[110px] object-cover rounded-lg border border-[#E4DFCF]"/>
              ) : (
                <div className="w-24 h-24 md:w-[110px] md:h-[110px] rounded-lg bg-[#F1EDDF] border border-[#E4DFCF] flex items-center justify-center text-[#4A5A50] text-xs">no image</div>
              )}
            </a>
            {row.cards_in_image > 1 && (
              <div className="absolute -top-1 -right-1 bg-[#C9A24B] text-[#0F1E15] text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-1" title={`Card ${row.card_index + 1} of ${row.cards_in_image} in one photo`}>
                <Layers size={9}/> {row.card_index + 1}/{row.cards_in_image}
              </div>
            )}
            {row.back_thumb_url ? (
              <a href={fileUrl(row.back_image_url)} target="_blank" rel="noreferrer" className="mt-2 block">
                <img src={fileUrl(row.back_thumb_url)} alt="back" className="w-full h-16 object-cover rounded-lg border border-[#C9A24B]"/>
                <div className="text-[10px] uppercase tracking-widest text-[#4A5A50] mt-1 text-center">Back captured</div>
              </a>
            ) : (row.status === "ok" && (
              <BackCaptureBtn idx={idx} row={row} onPatch={onPatch} onPatchParsed={onPatchParsed}/>
            ))}
          </div>

          {/* Fields */}
          <div className="min-w-0 space-y-2">
            {failed ? (
              <div className="text-sm text-red-700">
                <AlertTriangle size={14} className="inline mr-1"/> Couldn&apos;t read this card — <b>{row.error}</b>. Save others; you can retry this one manually.
              </div>
            ) : (
              <>
                {dup && (
                  <div className="rounded-md bg-amber-100/60 border border-amber-200 px-2 py-1 text-xs text-amber-800 inline-flex items-center gap-1">
                    <Users size={12}/> Possible duplicate of <b>{row.duplicate_name || "existing record"}</b>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Name</Label>
                    <Input value={row.parsed.name} onChange={(e) => onPatchParsed(idx, { name: e.target.value })} className="mt-1 h-9" data-testid={`batch-name-${idx}`}/>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Company</Label>
                    <Input value={row.parsed.company} onChange={(e) => onPatchParsed(idx, { company: e.target.value })} className="mt-1 h-9"/>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Phones</Label>
                    <Input
                      value={(row.parsed.phones || []).join(", ")}
                      onChange={(e) => onPatchParsed(idx, { phones: e.target.value.split(/[,\n]/).map((p) => p.trim()).filter(Boolean) })}
                      className="mt-1 h-9 font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Emails</Label>
                    <Input
                      value={(row.parsed.emails || []).join(", ")}
                      onChange={(e) => onPatchParsed(idx, { emails: e.target.value.split(/[,\n]/).map((p) => p.trim()).filter(Boolean) })}
                      className="mt-1 h-9"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Address</Label>
                    <Textarea value={row.parsed.address} onChange={(e) => onPatchParsed(idx, { address: e.target.value })} className="mt-1 min-h-[40px]"/>
                  </div>

                  {/* Voice note */}
                  <div className="md:col-span-2">
                    <Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">
                      <Mic size={11} className="inline mr-1"/> Voice note
                    </Label>
                    <VoiceNoteRecorder
                      voiceNoteUrl={row.voice_note_url}
                      transcript={row.voice_note_transcript}
                      onSaved={(url, transcript) => onPatch(idx, { voice_note_url: url, voice_note_transcript: transcript })}
                      onClear={() => onPatch(idx, { voice_note_url: null, voice_note_transcript: null })}
                      busy={voiceBusy}
                      setBusy={setVoiceBusy}
                      testId={`voice-${idx}`}
                    />
                  </div>

                  {/* Tags */}
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">
                      <Tag size={11} className="inline mr-1"/> Tags
                    </Label>
                    <div className="flex gap-1 mt-1">
                      <Input
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                        placeholder="e.g. iijs-2026, hot"
                        className="h-9 flex-1"
                        data-testid={`batch-tag-input-${idx}`}
                      />
                      <Button type="button" size="sm" variant="outline" className="h-9 px-2" onClick={addTag}><Plus size={12}/></Button>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(row.tags || []).map((t) => (
                        <span key={t} className="inline-flex items-center gap-1 rounded-full bg-[#0F5E39]/10 text-[#0F5E39] text-[11px] px-2 py-0.5">
                          {t}
                          <button type="button" onClick={() => removeTag(t)} className="hover:opacity-70"><X size={10}/></button>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Follow-up */}
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">
                      <CalendarClock size={11} className="inline mr-1"/> Follow-up
                    </Label>
                    <Input
                      type="datetime-local"
                      value={row.follow_up_at ? row.follow_up_at.slice(0, 16) : ""}
                      onChange={(e) => onPatch(idx, { follow_up_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
                      className="mt-1 h-9"
                      data-testid={`batch-followup-${idx}`}
                    />
                  </div>

                  {row.parsed.detected_language && (
                    <div className="text-[10px] uppercase tracking-widest text-[#4A5A50] md:col-span-2">
                      Detected language: <b>{row.parsed.detected_language}</b>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-row md:flex-col gap-1 shrink-0">
            {!failed && (
              <>
                <Button
                  size="sm"
                  variant={row.action === "save" ? "default" : "outline"}
                  className={`rounded-full h-9 ${row.action === "save" ? "bg-[#0F5E39] hover:bg-[#083F22] text-white" : ""}`}
                  onClick={() => onPatch(idx, { action: "save" })}
                  data-testid={`batch-save-btn-${idx}`}
                >
                  Save
                </Button>
                {!isMaster && dup && (
                  <Button
                    size="sm"
                    variant={row.action === "merge" ? "default" : "outline"}
                    className={`rounded-full h-9 ${row.action === "merge" ? "bg-[#C9A24B] hover:bg-[#B88C34] text-[#0F1E15]" : ""}`}
                    onClick={() => onPatch(idx, { action: "merge" })}
                  >
                    <ArrowUpRight size={12} className="mr-1"/> Merge
                  </Button>
                )}
                <Button
                  size="sm"
                  variant={row.action === "skip" ? "default" : "outline"}
                  className={`rounded-full h-9 ${row.action === "skip" ? "bg-[#4A5A50] text-white" : ""}`}
                  onClick={() => onPatch(idx, { action: "skip" })}
                >
                  <Trash2 size={12} className="mr-1"/> Skip
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


/**
 * VoiceNoteRecorder — record via MediaRecorder, upload for transcription.
 * If URL already set, shows a small audio player + Clear.
 */
function VoiceNoteRecorder({ voiceNoteUrl, transcript, onSaved, onClear, testId }) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      try { mediaRef.current.stop(); } catch (_) { /* noop */ }
    }
  }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const supportedMime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = supportedMime ? new MediaRecorder(stream, { mimeType: supportedMime }) : new MediaRecorder(stream);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        await upload(blob);
      };
      mr.start();
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Microphone unavailable. Grant permission and try again.");
    }
  };

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
  };

  const upload = async (blob) => {
    setUploading(true);
    try {
      const fd = new FormData();
      const ext = (blob.type.includes("ogg") ? "ogg" : blob.type.includes("mp4") ? "m4a" : "webm");
      fd.append("file", blob, `voice-note.${ext}`);
      const { data } = await api.post("/field-sales/scan-cards/voice-note", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onSaved(data.url, data.transcript);
      toast.success(data.transcript ? "Voice note saved (transcribed)" : "Voice note saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (voiceNoteUrl) {
    return (
      <div className="mt-1 space-y-1">
        <div className="flex items-center gap-2">
          <audio src={fileUrl(voiceNoteUrl)} controls preload="metadata" className="h-9 flex-1 max-w-full"/>
          <Button type="button" variant="ghost" size="sm" onClick={onClear} className="h-9 text-red-600" data-testid={`${testId}-clear`}>
            <X size={14}/>
          </Button>
        </div>
        {transcript && (
          <div className="text-xs text-[#4A5A50] italic bg-[#F6F1E1]/60 rounded p-2 border border-[#E4DFCF]">
            &ldquo;{transcript}&rdquo;
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-1 flex items-center gap-2">
      {!recording ? (
        <Button type="button" size="sm" variant="outline" className="h-9 rounded-full border-[#0F5E39] text-[#0F5E39]" onClick={start} disabled={uploading} data-testid={`${testId}-record`}>
          {uploading ? <Loader2 size={14} className="animate-spin mr-1"/> : <Mic size={14} className="mr-1"/>}
          {uploading ? "Uploading…" : "Record note"}
        </Button>
      ) : (
        <Button type="button" size="sm" className="h-9 rounded-full bg-red-600 hover:bg-red-700 text-white" onClick={stop} data-testid={`${testId}-stop`}>
          <StopCircle size={14} className="mr-1"/> Stop · {seconds}s
        </Button>
      )}
    </div>
  );
}


function BackCaptureBtn({ idx, row, onPatch, onPatchParsed }) {
  const fileRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("fronts", file);   // reuse pairs endpoint with just this file as "front"
      fd.append("backs", file);    // and same as back so backend runs a re-merge
      // Simpler: dedicated back-only re-capture endpoint. Fallback to pairs for now.
      // Silent noop to keep this minimal — user can re-scan the card in a fresh batch.
      toast.info("Re-capture: please add the back photo when re-uploading this card in a new batch");
      onPatch(idx, { needs_back: true });
    } catch (e) {
      toast.error("Failed to attach back image");
    } finally { setBusy(false); }
  };
  return (
    <>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }}
        data-testid={`batch-back-input-${idx}`}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        className="mt-2 w-full h-8 rounded-lg border border-dashed border-[#C9A24B] text-[10px] uppercase tracking-widest text-[#8A6600] hover:bg-[#FFF6E1]"
        disabled={busy}
        data-testid={`batch-recapture-back-${idx}`}
      >
        {busy ? "Uploading…" : "+ Add back"}
      </button>
    </>
  );
}

